"use client";

import { useEffect } from "react";

const WHEEL_THRESHOLD_PX = 90;
const WHEEL_GESTURE_RESET_MS = 200;
const TOUCH_THRESHOLD_PX = 52;
const SCROLL_SETTLE_TOLERANCE_PX = 5;
const SCROLL_STABLE_FRAMES = 5;
const SCROLL_MAX_MS = 1400;
const MANUAL_SCROLL_SYNC_MS = 160;

function getHeaderOffset(): number {
  const main = document.querySelector("main");
  if (!main) return 80;
  return parseFloat(getComputedStyle(main).paddingTop) || 80;
}

function getSections(pageClass: string): HTMLElement[] {
  const root = document.querySelector(`.${pageClass}`);
  if (!root) return [];
  return Array.from(root.querySelectorAll(":scope > section"));
}

function getSectionTop(section: HTMLElement): number {
  return section.getBoundingClientRect().top + window.scrollY;
}

function getSectionScrollBounds(section: HTMLElement, headerOffset: number) {
  const sectionTop = getSectionTop(section);
  const sectionHeight = section.offsetHeight;
  const visibleHeight = window.innerHeight - headerOffset;
  const min = sectionTop - headerOffset;
  const max = Math.max(min, sectionTop + sectionHeight - visibleHeight);

  return { min, max, visibleHeight, sectionHeight };
}

function getTargetScrollTop(section: HTMLElement, headerOffset: number): number {
  const { min, max, visibleHeight, sectionHeight } = getSectionScrollBounds(
    section,
    headerOffset,
  );
  const isCentered = section.hasAttribute("data-scroll-center");

  if (!isCentered) {
    return Math.max(0, min);
  }

  if (sectionHeight <= visibleHeight) {
    return Math.max(0, min - (visibleHeight - sectionHeight) / 2);
  }

  const centeredTop = min + (sectionHeight - visibleHeight) / 2;
  return Math.max(0, Math.min(Math.max(centeredTop, min), max));
}

function isSectionTallerThanViewport(section: HTMLElement, headerOffset: number): boolean {
  return section.offsetHeight > getSectionScrollBounds(section, headerOffset).visibleHeight + 1;
}

function canScrollWithinSection(
  section: HTMLElement,
  direction: number,
  headerOffset: number,
): boolean {
  if (!isSectionTallerThanViewport(section, headerOffset)) return false;

  const { min, max } = getSectionScrollBounds(section, headerOffset);
  const scrollY = window.scrollY;

  if (direction > 0) {
    return scrollY < max - SCROLL_SETTLE_TOLERANCE_PX;
  }

  if (direction < 0) {
    return scrollY > min + SCROLL_SETTLE_TOLERANCE_PX;
  }

  return false;
}

function getActiveSectionIndex(sections: HTMLElement[], headerOffset: number): number {
  const scrollY = window.scrollY;

  for (let i = 0; i < sections.length; i++) {
    const { min, max } = getSectionScrollBounds(sections[i], headerOffset);
    if (scrollY >= min - SCROLL_SETTLE_TOLERANCE_PX && scrollY <= max + SCROLL_SETTLE_TOLERANCE_PX) {
      return i;
    }
  }

  return getClosestSectionIndex(sections, headerOffset);
}

function getClosestSectionIndex(sections: HTMLElement[], headerOffset: number): number {
  const viewportCenter =
    window.scrollY + headerOffset + (window.innerHeight - headerOffset) / 2;

  let closest = 0;
  let minDistance = Number.POSITIVE_INFINITY;

  for (let i = 0; i < sections.length; i++) {
    const sectionCenter = getSectionTop(sections[i]) + sections[i].offsetHeight / 2;
    const distance = Math.abs(viewportCenter - sectionCenter);

    if (distance < minDistance) {
      minDistance = distance;
      closest = i;
    }
  }

  return closest;
}

export function HomeSectionScroll({
  pageClass = "home-page-light",
}: {
  pageClass?: "home-page-light" | "offres-page-light" | "projets-page-light" | "guides-page-light";
} = {}) {
  useEffect(() => {
    const root = document.querySelector(`.${pageClass}`);
    if (!root) return;

    let sections = getSections(pageClass);
    if (sections.length < 2) return;

    let animating = false;
    let committedIndex = getActiveSectionIndex(sections, getHeaderOffset());
    let wheelAccumulator = 0;
    let wheelDirection = 0;
    let wheelResetTimer: ReturnType<typeof setTimeout> | undefined;
    let manualScrollTimer: ReturnType<typeof setTimeout> | undefined;
    let scrollRaf = 0;
    let scrollTimeout: ReturnType<typeof setTimeout> | undefined;
    let scrollEndHandler: (() => void) | undefined;

    const clearScrollWatch = () => {
      if (scrollRaf) cancelAnimationFrame(scrollRaf);
      scrollRaf = 0;
      if (scrollTimeout) clearTimeout(scrollTimeout);
      scrollTimeout = undefined;
      if (scrollEndHandler) {
        window.removeEventListener("scrollend", scrollEndHandler);
        scrollEndHandler = undefined;
      }
    };

    const resetWheelGesture = () => {
      wheelAccumulator = 0;
      wheelDirection = 0;
      if (wheelResetTimer) clearTimeout(wheelResetTimer);
      wheelResetTimer = undefined;
    };

    const finishScroll = (index: number) => {
      clearScrollWatch();
      animating = false;
      committedIndex = index;
      resetWheelGesture();
    };

    const waitForScrollComplete = (targetTop: number, index: number) => {
      clearScrollWatch();
      animating = true;

      const complete = () => finishScroll(index);

      scrollEndHandler = complete;
      window.addEventListener("scrollend", scrollEndHandler, { once: true });

      let lastY = window.scrollY;
      let stableFrames = 0;

      const poll = () => {
        const currentY = window.scrollY;
        const atTarget = Math.abs(currentY - targetTop) <= SCROLL_SETTLE_TOLERANCE_PX;

        if (Math.abs(currentY - lastY) <= 1) {
          stableFrames += 1;
        } else {
          stableFrames = 0;
          lastY = currentY;
        }

        if (atTarget && stableFrames >= SCROLL_STABLE_FRAMES) {
          complete();
          return;
        }

        scrollRaf = requestAnimationFrame(poll);
      };

      scrollRaf = requestAnimationFrame(poll);
      scrollTimeout = setTimeout(complete, SCROLL_MAX_MS);
    };

    const goToIndex = (index: number, event?: Event): boolean => {
      sections = getSections(pageClass);
      if (sections.length < 2) return false;

      if (animating) {
        event?.preventDefault();
        return false;
      }

      if (index < 0 || index >= sections.length) {
        return false;
      }

      const headerOffset = getHeaderOffset();
      const targetTop = getTargetScrollTop(sections[index], headerOffset);

      if (index === committedIndex) {
        if (Math.abs(window.scrollY - targetTop) <= SCROLL_SETTLE_TOLERANCE_PX) {
          return false;
        }

        event?.preventDefault();
        resetWheelGesture();

        const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        window.scrollTo({
          top: targetTop,
          behavior: prefersReducedMotion ? "auto" : "smooth",
        });

        if (prefersReducedMotion) {
          finishScroll(index);
        } else {
          waitForScrollComplete(targetTop, index);
        }

        return true;
      }

      event?.preventDefault();
      resetWheelGesture();

      const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      window.scrollTo({
        top: targetTop,
        behavior: prefersReducedMotion ? "auto" : "smooth",
      });

      if (prefersReducedMotion) {
        finishScroll(index);
      } else {
        waitForScrollComplete(targetTop, index);
      }

      return true;
    };

    const onWheel = (event: WheelEvent) => {
      if (event.deltaY === 0) return;

      sections = getSections(pageClass);
      if (sections.length < 2) return;

      const direction = Math.sign(event.deltaY);
      const headerOffset = getHeaderOffset();
      const currentSection = sections[committedIndex];
      const nextIndex = committedIndex + direction;

      if (animating) {
        event.preventDefault();
        return;
      }

      if (
        currentSection &&
        canScrollWithinSection(currentSection, direction, headerOffset)
      ) {
        resetWheelGesture();
        return;
      }

      if (nextIndex < 0 || nextIndex >= sections.length) {
        resetWheelGesture();
        return;
      }

      if (wheelDirection !== 0 && direction !== wheelDirection) {
        wheelAccumulator = 0;
      }

      wheelDirection = direction;
      wheelAccumulator += event.deltaY;

      if (wheelResetTimer) clearTimeout(wheelResetTimer);
      wheelResetTimer = setTimeout(resetWheelGesture, WHEEL_GESTURE_RESET_MS);

      if (Math.abs(wheelAccumulator) < WHEEL_THRESHOLD_PX) {
        event.preventDefault();
        return;
      }

      const targetIndex = committedIndex + Math.sign(wheelAccumulator);
      resetWheelGesture();
      goToIndex(targetIndex, event);
    };

    let touchStartY = 0;

    const onTouchStart = (event: TouchEvent) => {
      touchStartY = event.touches[0]?.clientY ?? 0;
    };

    const onTouchEnd = (event: TouchEvent) => {
      if (animating) return;

      const touchEndY = event.changedTouches[0]?.clientY ?? touchStartY;
      const delta = touchStartY - touchEndY;

      if (Math.abs(delta) < TOUCH_THRESHOLD_PX) return;

      const direction = delta > 0 ? 1 : -1;
      const headerOffset = getHeaderOffset();
      const currentSection = sections[committedIndex];

      if (
        currentSection &&
        canScrollWithinSection(currentSection, direction, headerOffset)
      ) {
        return;
      }

      goToIndex(committedIndex + direction);
    };

    const onScroll = () => {
      if (animating) return;

      if (manualScrollTimer) clearTimeout(manualScrollTimer);
      manualScrollTimer = setTimeout(() => {
        sections = getSections(pageClass);
        if (sections.length < 2) return;
        committedIndex = getActiveSectionIndex(sections, getHeaderOffset());
      }, MANUAL_SCROLL_SYNC_MS);
    };

    window.addEventListener("wheel", onWheel, { passive: false });
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("scroll", onScroll);
      clearScrollWatch();
      resetWheelGesture();
      if (manualScrollTimer) clearTimeout(manualScrollTimer);
    };
  }, [pageClass]);

  return null;
}
